import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const complaintSchema = z.object({
  customer_id: z.string().min(1, 'Customer ID is required'),
  customer_contact: z.string().min(1, 'Email is required'),
  product_id: z.string().min(1, 'Product ID is required'),
  batch_id: z.string().min(1, 'Batch ID is required'),
  production_date: z.string().min(1, 'Production date is required'),
  defect_description: z.string().min(10, 'Defect description must be at least 10 characters'),
  severity: z.enum(['Low', 'Medium', 'High', 'Critical']),
  business_impact: z.string().min(1, 'Business impact is required'),
  cost_impact_estimate: z.string().min(1, 'Cost impact estimate is required'),
});

type ComplaintFormData = z.infer<typeof complaintSchema>;

interface CustomerComplaintFormProps {
  onSubmit: (data: ComplaintFormData) => void;
  isLoading?: boolean;
}

export const CustomerComplaintForm: React.FC<CustomerComplaintFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ComplaintFormData>({
    resolver: zodResolver(complaintSchema),
  });

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Customer Complaint Form</CardTitle>
        <CardDescription>
          Please fill out all required fields to submit a customer complaint.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Customer Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="customer_id">Customer ID *</Label>
                <Input
                  id="customer_id"
                  type="number"
                  placeholder="Enter customer ID"
                  {...register('customer_id')}
                />
                {errors.customer_id && (
                  <p className="text-sm text-destructive">{errors.customer_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_contact">Email *</Label>
                <Input
                  id="customer_contact"
                  placeholder="Enter Email information"
                  {...register('customer_contact')}
                />
                {errors.customer_contact && (
                  <p className="text-sm text-destructive">{errors.customer_contact.message}</p>
                )}
              </div>
            </div>

            {/* Product Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Product Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="product_id">Product ID *</Label>
                <Input
                  id="product_id"
                  type="number"
                  placeholder="Enter product ID"
                  {...register('product_id')}
                />
                {errors.product_id && (
                  <p className="text-sm text-destructive">{errors.product_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="batch_id">Batch ID *</Label>
                <Input
                  id="batch_id"
                  type="number"
                  placeholder="Enter batch ID"
                  {...register('batch_id')}
                />
                {errors.batch_id && (
                  <p className="text-sm text-destructive">{errors.batch_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="production_date">Production Date *</Label>
                <Input
                  id="production_date"
                  type="date"
                  {...register('production_date')}
                />
                {errors.production_date && (
                  <p className="text-sm text-destructive">{errors.production_date.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Complaint Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Complaint Details</h3>
            
            <div className="space-y-2">
              <Label htmlFor="defect_description">Defect Description *</Label>
              <Textarea
                id="defect_description"
                placeholder="Describe the defect in detail (minimum 10 characters)"
                rows={4}
                {...register('defect_description')}
              />
              {errors.defect_description && (
                <p className="text-sm text-destructive">{errors.defect_description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="severity">Severity Level *</Label>
                <Select onValueChange={(value) => setValue('severity', value as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select severity level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
                {errors.severity && (
                  <p className="text-sm text-destructive">{errors.severity.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost_impact_estimate">Cost Impact Estimate ($) *</Label>
                <Input
                  id="cost_impact_estimate"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  {...register('cost_impact_estimate')}
                />
                {errors.cost_impact_estimate && (
                  <p className="text-sm text-destructive">{errors.cost_impact_estimate.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_impact">Business Impact *</Label>
              <Textarea
                id="business_impact"
                placeholder="Describe the impact on business operations"
                rows={3}
                {...register('business_impact')}
              />
              {errors.business_impact && (
                <p className="text-sm text-destructive">{errors.business_impact.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Submitting...' : 'Submit Complaint'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};